import {OCR_CHECKING_STATUSES, type OCRDetectedData} from '@chekin/core';

export const OCR_FAILURE_REASONS = {
  serviceError: 'service_error',
  notRecognized: 'not_recognized',
  codeNotRead: 'code_not_read',
  codeSideMissed: 'code_side_missed',
  documentCropped: 'document_cropped',
  imageBlurry: 'image_blurry',
  imageGlare: 'image_glare',
  imageAngle: 'image_angle',
  poorLighting: 'poor_lighting',
  lowResolution: 'low_resolution',
} as const;

export type OCRFailureReason =
  (typeof OCR_FAILURE_REASONS)[keyof typeof OCR_FAILURE_REASONS];

export const OCR_REASON_CODES = {
  serviceUnavailable: 'service_unavailable',
  processingFailed: 'processing_failed',
  documentNotRecognized: 'document_not_recognized',
  mrzNotFound: 'mrz_not_found',
  documentCropped: 'document_cropped',
  imageBlurry: 'image_blurry',
  imageGlare: 'image_glare',
  imageAngle: 'image_angle',
  poorLighting: 'poor_lighting',
  lowResolution: 'low_resolution',
} as const;

const REASON_CODE_TO_FAILURE: Record<string, OCRFailureReason> = {
  [OCR_REASON_CODES.serviceUnavailable]: OCR_FAILURE_REASONS.serviceError,
  [OCR_REASON_CODES.processingFailed]: OCR_FAILURE_REASONS.serviceError,
  [OCR_REASON_CODES.documentNotRecognized]: OCR_FAILURE_REASONS.notRecognized,
  [OCR_REASON_CODES.mrzNotFound]: OCR_FAILURE_REASONS.codeNotRead,
  [OCR_REASON_CODES.documentCropped]: OCR_FAILURE_REASONS.documentCropped,
  [OCR_REASON_CODES.imageBlurry]: OCR_FAILURE_REASONS.imageBlurry,
  [OCR_REASON_CODES.imageGlare]: OCR_FAILURE_REASONS.imageGlare,
  [OCR_REASON_CODES.imageAngle]: OCR_FAILURE_REASONS.imageAngle,
  [OCR_REASON_CODES.poorLighting]: OCR_FAILURE_REASONS.poorLighting,
  [OCR_REASON_CODES.lowResolution]: OCR_FAILURE_REASONS.lowResolution,
};

type OCRFailureSource = {
  status?: string;
  reason?: OCRDetectedData['reason'];
  reason_code?: OCRDetectedData['reason_code'];
  picture_with_face?: OCRDetectedData['picture_with_face'];
};

export type OCRFailure = {
  reason: OCRFailureReason;
  titleKey: string;
  descriptionKey: string;
};

const FAILURE_COPY: Record<OCRFailureReason, Omit<OCRFailure, 'reason'>> = {
  [OCR_FAILURE_REASONS.serviceError]: {
    titleKey: 'ocr_service_error_title',
    descriptionKey: 'ocr_service_error_text',
  },
  [OCR_FAILURE_REASONS.notRecognized]: {
    titleKey: 'ocr_not_recognized_title',
    descriptionKey: 'ocr_not_recognized_text',
  },
  [OCR_FAILURE_REASONS.codeNotRead]: {
    titleKey: 'ocr_code_not_read_title',
    descriptionKey: 'ocr_code_not_read_text',
  },
  [OCR_FAILURE_REASONS.codeSideMissed]: {
    titleKey: 'ocr_code_side_missed_title',
    descriptionKey: 'ocr_code_side_missed_text',
  },
  [OCR_FAILURE_REASONS.documentCropped]: {
    titleKey: 'ocr_document_cropped_title',
    descriptionKey: 'ocr_document_cropped_text',
  },
  [OCR_FAILURE_REASONS.imageBlurry]: {
    titleKey: 'ocr_image_blurry_title',
    descriptionKey: 'ocr_image_blurry_text',
  },
  [OCR_FAILURE_REASONS.imageGlare]: {
    titleKey: 'ocr_image_glare_title',
    descriptionKey: 'ocr_image_glare_text',
  },
  [OCR_FAILURE_REASONS.imageAngle]: {
    titleKey: 'ocr_image_angle_title',
    descriptionKey: 'ocr_image_angle_text',
  },
  [OCR_FAILURE_REASONS.poorLighting]: {
    titleKey: 'ocr_poor_lighting_title',
    descriptionKey: 'ocr_poor_lighting_text',
  },
  [OCR_FAILURE_REASONS.lowResolution]: {
    titleKey: 'ocr_low_resolution_title',
    descriptionKey: 'ocr_low_resolution_text',
  },
};

export function getOCRFailureReason(data: OCRFailureSource): OCRFailureReason {
  const reason = getReasonFromResult(data);
  if (reason === OCR_FAILURE_REASONS.codeNotRead && data.picture_with_face === 1) {
    return OCR_FAILURE_REASONS.codeSideMissed;
  }
  return reason;
}

function getReasonFromResult(data: OCRFailureSource): OCRFailureReason {
  const fromCode = data.reason_code && REASON_CODE_TO_FAILURE[data.reason_code];
  if (fromCode) {
    return fromCode;
  }
  if (data.status === OCR_CHECKING_STATUSES.error) {
    return OCR_FAILURE_REASONS.serviceError;
  }
  if (data.reason) {
    return OCR_FAILURE_REASONS.notRecognized;
  }
  return OCR_FAILURE_REASONS.codeNotRead;
}

export function getOCRFailure(data: OCRFailureSource): OCRFailure {
  const reason = getOCRFailureReason(data);
  return {reason, ...FAILURE_COPY[reason]};
}
